import { useEffect, useRef, useState } from "react";

import {
  buildStudyUpdatePayload,
  getProjectBySlug,
  getStudyById,
  getStudyBySlug,
  mapStudyToFormData,
  regenerateStudyPlan,
  streamStudyHelperChat,
  updateStudy,
} from "../services/studyService";
import { useAppContext } from "../../../context/AppContext";
import { useB2bTokenQuota } from "../../persona/hooks/useB2bTokenQuota";
import { shouldBlockTokenGatedStudyFeature } from "../../../utils/productAccess";

const initialFormData = {
  studyName: "",
  problemDefinition: "",
  methodologies: [],
  timeline: "",
  startDate: "",
  endDate: "",
  budget: "",
  targetAudience: "",
  participantCount: "",
  additionalRequirements: "",
};

const initialChatMessages = [
  {
    type: "assistant",
    content: "안녕하세요! \n궁금한 점이 있으시면 언제든 물어보세요!",
  },
];

export const methodologyOptions = [
  "심층 인터뷰",
  "사용성 테스트",
  "포커스 그룹",
  "관찰 조사",
  "카드 소팅",
  "다이어리 스터디",
];

export const useStudyEditPage = ({
  projectSlug,
  studySlug,
  navigate,
}) => {
  const { user, hasMinimumTier } = useAppContext();
  const { isQuotaExceeded } = useB2bTokenQuota();
  const [projectId, setProjectId] = useState(null);
  const [studyId, setStudyId] = useState(null);
  const [project, setProject] = useState(null);
  const [study, setStudy] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [editingFields, setEditingFields] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isArtifactDeleted, setIsArtifactDeleted] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState({
    isOpen: false,
    reason: "feature",
  });
  const [toast, setToast] = useState({
    isOpen: false,
    type: "success",
    message: "",
  });
  const [chatMessages, setChatMessages] = useState(initialChatMessages);
  const [chatInput, setChatInput] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const chatEndRef = useRef(null);
  const chatInputRef = useRef(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [expandedSet, setExpandedSet] = useState(new Set());
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customMethodology, setCustomMethodology] = useState("");
  const [isCustomMethodologyComposing, setIsCustomMethodologyComposing] =
    useState(false);
  const [loading, setLoading] = useState(true);

  const isExpanded = (index) => expandedSet.has(index);

  const toggleExpand = (index) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setIsArtifactDeleted(urlParams.get("deleted") === "true");
  }, []);

  useEffect(() => {
    const loadBySlug = async () => {
      try {
        if (projectSlug) {
          const projectData = await getProjectBySlug(projectSlug);
          if (projectData) {
            setProject(projectData);
            setProjectId(projectData.id);
          }
        }

        if (studySlug) {
          const studyData = await getStudyBySlug(studySlug);
          if (studyData) {
            setStudyId(studyData.id);
            setStudy(studyData);
            setFormData(mapStudyToFormData(studyData));
            setLoading(false);
          }
        }
      } catch (error) {
        console.error("Slug 조회 실패:", error);
      }
    };

    loadBySlug();
  }, [projectSlug, studySlug]);

  useEffect(() => {
    if (!studyId || study) return;

    const loadStudyData = async () => {
      try {
        const studyData = await getStudyById(studyId);
        if (studyData) {
          setStudy(studyData);
          setFormData(mapStudyToFormData(studyData));
        }
      } catch (error) {
        console.error("연구 데이터 로드 실패:", error);
      } finally {
        setLoading(false);
      }
    };

    loadStudyData();
  }, [studyId, study]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  useEffect(() => {
    if (chatInputRef.current) {
      chatInputRef.current.style.height = "auto";
      chatInputRef.current.style.height = `${Math.min(
        chatInputRef.current.scrollHeight,
        120
      )}px`;
    }
  }, [chatInput]);

  useEffect(() => {
    const adjustTextareaHeight = (textarea) => {
      if (!textarea) return;
      textarea.style.height = "auto";
      const minHeight = textarea.classList.contains("auto-textarea") ? 40 : 44;
      textarea.style.height = `${Math.max(textarea.scrollHeight, minHeight)}px`;
    };

    const textareas = document.querySelectorAll(
      ".card-textarea, .card-input.auto-textarea"
    );
    textareas.forEach(adjustTextareaHeight);
  }, [editingFields]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleTextareaChange = (field, value, event) => {
    handleInputChange(field, value);
    if (event?.target) {
      event.target.style.height = "auto";
      const minHeight = event.target.classList.contains("auto-textarea") ? 40 : 44;
      event.target.style.height = `${Math.max(
        event.target.scrollHeight,
        minHeight
      )}px`;
    }
  };

  const toggleEditMode = (field) => {
    setEditingFields((prev) => {
      if (field === "methodologies" && prev[field]) {
        setShowCustomInput(false);
        setCustomMethodology("");
      }
      return {
        ...prev,
        [field]: !prev[field],
      };
    });
  };

  const handleMethodologyToggle = (methodology) => {
    setFormData((prev) => ({
      ...prev,
      methodologies: prev.methodologies.includes(methodology)
        ? prev.methodologies.filter((item) => item !== methodology)
        : [...prev.methodologies, methodology],
    }));
    setHasChanges(true);
  };

  const handleAddCustomMethodology = (method) => {
    const trimmed = method.trim();
    if (!trimmed) return;
    if (!formData.methodologies.includes(trimmed)) {
      setFormData((prev) => ({
        ...prev,
        methodologies: [...prev.methodologies, trimmed],
      }));
      setHasChanges(true);
    }
  };

  const closeEditMode = (field) => {
    setEditingFields((prev) => ({
      ...prev,
      [field]: false,
    }));
    if (field === "methodologies") {
      setShowCustomInput(false);
      setCustomMethodology("");
    }
  };

  const finishEdit = async (field) => {
    try {
      const updateData = buildStudyUpdatePayload(formData, study);
      await updateStudy(studyId, updateData);

      const updatedStudy = await getStudyById(studyId);
      if (updatedStudy) setStudy(updatedStudy);

      closeEditMode(field);
      setHasChanges(false);
    } catch (error) {
      console.error(`필드 ${field} 저장 오류:`, error);
      alert(`필드 저장 중 오류가 발생했습니다: ${error.message}`);
      closeEditMode(field);
    }
  };

  const sendChatMessage = async (opts = {}) => {
    const { mode = "general", task = null, overrideMessage = null } = opts;
    if (isChatLoading) return;
    const raw = overrideMessage ?? chatInput;
    if (!raw || !raw.trim()) return;

    const userMessage = raw.trim();
    setChatInput("");
    setIsChatLoading(true);

    if (isChatMinimized) {
      setIsChatMinimized(false);
    }

    setChatMessages((prev) => [...prev, { type: "user", content: userMessage }]);

    try {
      const response = await streamStudyHelperChat({
        message: userMessage,
        context: {
          currentForm: {
            ...formData,
            participantCount: parseInt(formData.participantCount, 10) || 0,
          },
          projectName: project?.name || "프로젝트",
        },
        mode,
        task,
      });

      if (!response.ok) throw new Error("챗봇 응답 실패");

      setChatMessages((prev) => [
        ...prev,
        { type: "assistant", content: "응답을 생성 중입니다..." },
      ]);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = "";
      const updateLastAssistantMessage = (content) => {
        setChatMessages((prev) => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1].content = content;
          return newMessages;
        });
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6);
            if (data === "[DONE]") break;

            try {
              const parsed = JSON.parse(data);
              if (parsed.content) {
                assistantMessage += parsed.content;
                updateLastAssistantMessage(assistantMessage);
              }
            } catch (error) {
              // Ignore malformed stream chunks.
            }
          }
        }
      }
    } catch (error) {
      console.error("챗봇 오류:", error);
      setChatMessages((prev) => [
        ...prev,
        {
          type: "assistant",
          content: "죄송합니다. 일시적인 오류가 발생했습니다. 다시 시도해주세요.",
        },
      ]);
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleRegeneratePlan = async () => {
    if (shouldBlockTokenGatedStudyFeature(user, { isQuotaExceeded, hasMinimumTier })) {
      setUpgradeModal({ isOpen: true, reason: "feature" });
      return;
    }

    if (!formData.problemDefinition.trim()) {
      alert("문제 정의는 필수 입력 항목입니다.");
      return;
    }

    try {
      const updateData = buildStudyUpdatePayload(formData, study);
      await updateStudy(studyId, updateData);

      const result = await regenerateStudyPlan(studyId, {
        formData,
        projectId,
        studyId,
      });

      if (result.success) {
        setHasChanges(false);
        const slug = result.study_slug || studySlug || result.study_id;
        navigate(`/study/${slug}`);
      } else {
        alert(`계획서 재생성에 실패했습니다: ${result.error || "알 수 없는 오류"}`);
      }
    } catch (error) {
      console.error("재생성 오류:", error);
      alert("계획서 재생성 중 오류가 발생했습니다.");
    }
  };

  const handleGoBack = () => {
    navigate(`/study/${studySlug}`);
  };

  return {
    chatEndRef,
    chatInputRef,
    chatInput,
    chatMessages,
    copiedIndex,
    customMethodology,
    editingFields,
    formData,
    hasChanges,
    isArtifactDeleted,
    isChatLoading,
    isChatMinimized,
    isCustomMethodologyComposing,
    loading,
    project,
    showCustomInput,
    study,
    toast,
    upgradeModal,
    finishEdit,
    handleAddCustomMethodology,
    handleGoBack,
    handleInputChange,
    handleMethodologyToggle,
    handleRegeneratePlan,
    handleTextareaChange,
    isExpanded,
    sendChatMessage,
    setChatInput,
    setCopiedIndex,
    setCustomMethodology,
    setEditingFields,
    setFormData,
    setIsChatMinimized,
    setIsCustomMethodologyComposing,
    setShowCustomInput,
    setToast,
    setUpgradeModal,
    toggleEditMode,
    toggleExpand,
  };
};
